import React from 'react';
import cx from 'classnames';

import PageLayout from '@magoosh/layouts/page';
import { PageProps } from '@magoosh/layouts/types';
import { formatPrice } from 'utilities/money';

import SectionHeader from '@marketing/components/section_header';
import FAQSection from '@marketing/sections/faq';
import { OfferProps } from '@marketing/types';
import { defaultRegistrationUrl } from '@marketing/utilities';

import TestimonialsSection from './sections/testimonials';

import CompetitorComparisonSection from '../sections/competitor_comparison';
import HeroSection from '../sections/hero';
import OffersSection from '../sections/offers';
import StartYourPrepSection from '../sections/start_your_prep';

import AITutorIcon from '@marketing/sections/value_props/assets/green/banner_check_icon.svg';
import PracticeQuestionsIcon from '@marketing/sections/value_props/assets/green/practice_questions_icon.svg';
import PracticeTestsIcon from '@marketing/sections/value_props/assets/green/practice_tests_icon.svg';
import ScorePredictorIcon from '@marketing/sections/value_props/assets/green/score_gauge_icon.svg';
import StudySchedulesIcon from '@marketing/sections/value_props/assets/green/timely_guidance_icon.svg';
import VideoLessonsIcon from '@marketing/sections/value_props/assets/green/video_lessons_icon.svg';

import BannerCheckIcon from '../sections/competitor_comparison/assets/banner_check_icon.svg';
import BubbleQuestionIcon from '../sections/competitor_comparison/assets/bubble_question_icon.svg';
import ChecklistIcon from '../sections/competitor_comparison/assets/checklist_icon.svg';
import CircleRefreshIcon from '../sections/competitor_comparison/assets/circle_refresh_icon.svg';
import DollarSignIcon from '../sections/competitor_comparison/assets/dollar_sign_icon.svg';
import FireworkIcon from '../sections/competitor_comparison/assets/firework_icon.svg';
import FlashcardsIcon from '../sections/competitor_comparison/assets/flashcards_icon.svg';
import HighFiveIcon from '../sections/competitor_comparison/assets/high_five_icon.svg';
import HourglassIcon from '../sections/competitor_comparison/assets/hourglass_icon.svg';
import MobileDeviceIcon from '../sections/competitor_comparison/assets/mobile_device_icon.svg';
import NotebookIcon from '../sections/competitor_comparison/assets/notebook_icon.svg';
import ScoreGaugeIcon from '../sections/competitor_comparison/assets/score_gauge_icon.svg';
import StarIcon from '../sections/competitor_comparison/assets/star_icon.svg';
import VideoPlayerIcon from '../sections/competitor_comparison/assets/video_player_icon.svg';

import styles from '../style.module.scss';
import marketingStyles from '@marketing/style.module.scss';

interface Props extends PageProps {
  hideSignUp: boolean;
  isLoggedIn: boolean;
  offers: OfferProps[];
}

export default (props: Props) => {
  const premium6MonthsOffer = props.offers.find((offer) => offer.slug === 'premium-6-month');
  const premiumOffer = props.offers.find((offer) => offer.slug === 'premium-1-month');
  const trialOffer = props.offers.find((offer) => offer.slug === 'trial');

  return (
    <div style={{ overflow: 'hidden', position: 'relative' }}>
      {/* Absolutely positioned background elements */}
      <>
        <div
          className={marketingStyles.greenGradientBg}
          style={{ position: 'absolute', height: 900, left: 0, top: 0, width: '100%' }}
        />
        <div className={styles.greenEllipse} />
      </>

      <PageLayout
        childrenStyle={{ paddingTop: 30 }}
        dashedBorder
        examName="IELTS"
        navigationItems={props.navigationItems}
        notice={props.notice}
      >

        <HeroSection
          examName="TOEFL"
          header={<>Get The IELTS<span className={styles.registeredMark}>®</span> Band Score You Need!</>}
          subHeader="7-Day Money Back Guarantee: Email us if you want to cancel for any reason. No strings attached."
          valueProps={[
            {
              description: "Up to 10 full-length mock tests with realistic writing and speaking",
              icon: PracticeTestsIcon,
              name: "Practice Tests"
            },
            {
              description: "Every question is created by an IELTS expert to mimic the actual exam",
              icon: PracticeQuestionsIcon,
              name: "850+ Practice Questions"
            },
            {
              description: "Prepare for every section, on any device and 100% online",
              icon: VideoLessonsIcon,
              name: "Expert Video Lessons"
            },
            {
              description: "Get unlimited feedback on your writing and speaking",
              icon: StudySchedulesIcon,
              name: "Graded Writing/Speaking"
            },
            {
              description: "Never get stuck with an AI tutor to explain any question or concept",
              icon: AITutorIcon,
              name: "AI Tutor"
            },
            {
              description: "Be confident when you're ready with our score predictor tool",
              icon: ScorePredictorIcon,
              name: "Accurate Score Predictor"
            }
          ]}
        />

        <OffersSection
          primaryOffer={{
            header: (
              <>
                <span className="text-bold text-white">Premium</span>
                {' '}<span style={{ color: '#8E70B2' }}>·</span>{' '}
                <span className="text-bold text-white">6 months</span>
              </>
            ),
            highlight: <><small><strong>Best option</strong> for most students</small></>,
            offer: premium6MonthsOffer,
            subHeader: 'Save $625 by purchasing 6 months at once',
            valuePropGroups: [
              {
                content: [
                  {
                    content: 'Comprehensive coverage of IELTS Speaking, Listening, Reading, and Writing',
                    tooltip: "Complete coverage of all sections of<br />the Academic and General Training IELTS to help<br />you learn the test and improve"
                  },
                  {
                    content: 'Up to 10 full-length mock tests',
                    tooltip: 'Our timed and scored full-length practice<br />tests are generated from your pool of<br />850 practice questions.'
                  },
                  {
                    content: 'Over 120 helpful video lessons',
                    tooltip: 'Watch lessson anytime of day on every<br />concept tested on both versions of the IELTS<br />exam and the strategies you need to know to improve'
                  },
                  {
                    content: '850 test-like practice questions with explanation videos',
                    tooltip: 'Every question is created by IELTS experts<br />to mimic the actual exam'
                  }
                ],
                header: 'Content'
              },
              {
                content: [
                  {
                    content: 'Unlimited AI Speaking Grading',
                    tooltip: 'Receive an instant band score estimate<br />and detailed feedback from Magoosh AI on<br />your Speaking interview'
                  },
                  {
                    content: 'Unlimited AI Writing Grading',
                    tooltip: 'Receive an instant band score estimate<br />and detailed feedback from Magoosh AI on<br />your Task 1 or Task 2 essay'
                  },
                  {
                    content: 'Score Predictor',
                    tooltip: 'A band score estimate for each section of<br />the exam updates as you learn and improve'
                  },
                  {
                    content: 'Detailed Study Schedules',
                    tooltip: 'Study schedules and checklists to keep you on track'
                  },
                  {
                    content: 'Unlimited Ask-an-Expert Questions',
                    tooltip: 'Quick email support from our IELTS experts<br />when you get stuck or just want some extra help'
                  },
                  {
                    content: 'Target Band Score Guarantee',
                    tooltip: "We are confident you will achieve your<br />targetband score with Magoosh. If you don't,<br />we'll give you a full refund."
                  }
                ],
                header: 'Features'
              }
            ]
          }}
          secondaryOffer={{
            header: (
              <>
                <span className="text-bold text-gray-darker">Premium</span>
                {' '}<span style={{ color: '#9BBCAF' }}>·</span>{' '}
                <span className="text-bold text-gray-darker">1 month</span>
              </>
            ),
            offer: premiumOffer,
            subHeader: 'Great option for students with limited study time',
            valuePropGroups: [
              {
               content: [
                  {
                    content: 'Comprehensive coverage of IELTS Speaking, Listening, Reading, and Writing',
                    tooltip: "Complete coverage of all sections of<br />the Academic and General Training IELTS to help<br />you learn the test and improve"
                  },
                  {
                    content: 'Up to 10 full-length mock tests',
                    tooltip: 'Our timed and scored full-length practice<br />tests are generated from your pool of<br />850 practice questions.'
                  },
                  {
                    content: 'Over 120 helpful video lessons',
                    tooltip: 'Watch lessson anytime of day on every<br />concept tested on both versions of the IELTS<br />exam and the strategies you need to know to improve'
                  },
                  {
                    content: '850 test-like practice questions with explanation videos',
                    tooltip: 'Every question is created by IELTS experts<br />to mimic the actual exam'
                  }
                ],
                header: 'Content'
              },
              {
                content: [
                  {
                    content: 'Unlimited AI Speaking Grading',
                    tooltip: 'Receive an instant band score estimate<br />and detailed feedback from Magoosh AI on<br />your Speaking interview'
                  },
                  {
                    content: 'Unlimited AI Writing Grading',
                    tooltip: 'Receive an instant band score estimate<br />and detailed feedback from Magoosh AI on<br />your Task 1 or Task 2 essay'
                  },
                  {
                    content: 'Score Predictor',
                    tooltip: 'A band score estimate for each section of<br />the exam updates as you learn and improve'
                  },
                  {
                    content: 'Detailed Study Schedules',
                    tooltip: 'Study schedules and checklists to keep you on track'
                  },
                  {
                    content: 'Unlimited Ask-an-Expert Questions',
                    tooltip: 'Quick email support from our IELTS experts<br />when you get stuck or just want some extra help'
                  },
                  {
                    content: 'Target Band Score Guarantee',
                    tooltip: "We are confident you will achieve your<br />targetband score with Magoosh. If you don't,<br />we'll give you a full refund."
                  }
                ],
                header: 'Features'
              }
            ]
          }}
          tertiaryOffer={null}
          trialOffer={{
            header: 'Try Magoosh for 7 days with no commitment',
            offer: trialOffer,
            subHeader: 'Sign up for a free trial and get to know some of our lesson videos and practice questions.'
          }}
        />

        <CompetitorComparisonSection
          companyFeatureOfferings={[
            {
              name: 'Magoosh',
              'Price': formatPrice(premium6MonthsOffer.fullPriceInCents, 'usd', true),
              'Length of Online Access': '6 months',
              'AI Tutor': 'Included',
              'Practice Questions and Exams': 'Included',
              'Writing Assessments': 'Included',
              'Speaking Assessments': 'Included',
              'Mobile App': 'Included',
              'Free Trial': 'Included',
              'Video Lessons': 'Included',
              'No Questions Asked Money-Back Guarantee': 'Included',
              'Score-Improvement Guarantee': 'Hit Your Target Guarantee',
              'Flashcards Included': 'Included',
              'Score Predictor': 'Included'
            },
            {
              name: 'BestMyTest',
              'Price': '$249',
              'Length of Online Access': '6 months',
              'Practice Questions and Exams': 'Included',
              'Writing Assessments': 'Included',
              'Speaking Assessments': 'Included',
              'Free Trial': 'Included',
              'Video Lessons': 'Included'
            },
            {
              name: 'E2Language',
              'Price': '$269',
              'Length of Online Access': '12 months',
              'Practice Questions and Exams': 'Included',
              'Writing Assessments': 'Included',
              'Speaking Assessments': 'Included',
              'Free Trial': 'Included',
              'Video Lessons': 'Included'
            }
          ]}
          features={[
            { icon: DollarSignIcon, name: 'Price' },
            { icon: HourglassIcon, name: 'Length of Online Access' },
            { icon: StarIcon, name: 'AI Tutor' },
            { icon: BubbleQuestionIcon, name: 'Practice Questions and Exams' },
            { icon: NotebookIcon, name: 'Writing Assessments' },
            { icon: ChecklistIcon, name: 'Speaking Assessments' },
            { icon: MobileDeviceIcon, name: 'Mobile App' },
            { icon: FireworkIcon, name: 'Free Trial' },
            { icon: VideoPlayerIcon, name: 'Video Lessons' },
            { icon: CircleRefreshIcon, name: 'No Questions Asked Money-Back Guarantee' },
            { icon: HighFiveIcon, name: 'Score-Improvement Guarantee' },
            { icon: FlashcardsIcon, name: 'Flashcards Included' },
            { icon: ScoreGaugeIcon, name: 'Score Predictor' }
          ]}
          registrationUrl={defaultRegistrationUrl(premiumOffer)}
        />

        <TestimonialsSection />

        <FAQSection
          entries={[
            {
              answer: (
                <>
                  <p>Anyone can use Magoosh, but it's best for individuals who fall into one of the following categories:</p>
                  <ul>
                    <li><b>Self-studiers:</b> those who prefer to study on their own time and at their own pace.</li>
                    <li><b>Video-lovers:</b> those who learn better by watching video than through reading or live-learning. Every practice question has a video or text explanation.</li>
                    <li><b>Busy students and professionals:</b> those who do not have much time to study and want to sneak in a practice session here and there.</li>
                  </ul>
                </>
              ),
              question: 'Who should use Magoosh?'
            },
            {
              answer: (
                <>
                  If you’re unhappy for any reason, email us within 7 days of the <i>original</i> purchase date (regardless if you pay in installments), and we will give you a full refund. No strings attached. If you purchase Magoosh using a non-US credit card and request a refund, sometimes your credit card company will keep the foreign currency transaction fee.
                </>
              ),
              question: 'How does the refund policy work?'
            },
            {
              answer: "We compare your performance in the program with data from Magoosh students' actual scores and performance in our program and then provide you with a predicted range for the actual test.",
              question: 'How does your score predictor work?'
            },
            {
              answer: (
                <>
                  Check out this <a href="https://magoosh.zendesk.com/hc/en-us/articles/360048879351">help article with a list of accessibility features!</a>
                </>
              ),
              question: 'What accessibility features are there?'
            },
            {
              answer: (
                <>
                  Which IELTS you take depends on your goals. You should find out from your program, job, or immigration service which test to take. If you think you may need to take both, send us an email and we'd be happy to give you access to both Academic and General Training materials. Send an email to <a href="mailto:help@magoosh.com">help@magoosh.com</a>. We'd love to hear from you and help.
                </>
              ),
              question: 'Should I sign up for the Academic of General Training IELTS?'
            },
            {
              answer: (
                <>
                  Magoosh content is all in English, so you'll need to have some experience with English. But you don't need to be an expert. We have features that will help you prepare for the IELTS, even if you aren't confident in your English level. We have transcripts and captions for our Lessons. You can also slowdown the speed of the lessons to make it easier to follow along. We recommend signing up for our <a href={defaultRegistrationUrl(trialOffer)}>free trial</a> and exploring the features to see if Magoosh is right for you.
                </>
              ),
              question: 'What English level should I have before using Magoosh IELTS?'
            },
            {
              answer: (
                <>
                  Many students are in this same position and we can help. Sign up for one, and if your plans change, we can easily switch your plan over to the other exam. Send an email to <a href="mailto:help@magoosh.com">help@magoosh.com</a> to find out more or switch your plan. We'd love to hear from you and help.
                </>
              ),
              question: 'I am not sure if I will take the TOEFL or the IELTS. What should I do?'
            },
            {
              answer: (
                <>
                  We guarantee you'll get <a href="/score-guarantee"><b>your target band score</b></a> using our Premium Magoosh product. If you don't, we'll give you all of your money back.
                </>
              ),
              question: 'How does the score guarantee work?'
            },
            {
              answer: (
                <>
                  Visa, Mastercard, American Express, Discover, Alipay, Apple Pay, Google Pay, and PayPal.
                </>
              ),
              question: 'What types of payment do you accept?'
            },
            {
              answer: (
                <>
                  <p>If you choose to pay in four monthly installments, your first payment is due at checkout, with the remaining installments charged monthly. You'll receive an email with your payment schedule after checkout.</p>
                  <p><strong>Please note:</strong> Installment plans are not subscriptions—payments continue even if you pause your plan, but your access will be extended by the paused duration. By choosing an installment plan, you are committing to paying the full purchase amount unless you request a refund within 7 days of your initial purchase. Pausing your plan does not extend the refund window.</p>
                </>
              ),
              question: 'How do monthly payments work?'
            },
            {
              answer: (
                <>
                  Yes, we do! You can access all of the lessons anywhere with our apps, and you can use your mobile browser to get full access to the site.
                </>
              ),
              question: 'Do you have iPad/iPhone/Android access?'
            },
            {
              answer: (
                <>
                  You definitely can! Email us at <a href="mailto:help@magoosh.com">help@magoosh.com</a> and we'll help set you up.
                </>
              ),
              question: 'Can I purchase multiple accounts for my students or non-profit?'
            },
            {
              answer: (
                <>
                  It’s 100% online. No DVDs. No books. It's all online, available any time of day, so you can practice taking the test on a computer, just like you'll have to do on your exam day for the real IELTS. Magoosh works on both Macs and PCs: any device with an internet connection!
                </>
              ),
              question: 'How does the course work?'
            },
            {
              answer: (
                <>
                  With Magoosh Premium, you'll have access to full-length practice tests that accurately reflect what you'll see on test day. The tests are generated from your pool of unanswered questions.
                </>
              ),
              question: 'Do I get practice tests?'
            },
            {
              answer: (
                <>
                  We’re happy to help you with questions you have about any of our material. Send us a message while you're studying, and we'll get back to you quickly. We do have limitations to how much we can help any individual student, but in most cases (for 99% of students), we can answer all of your questions!
                </>
              ),
              question: "If I'm struggling with a question, can I get help?"
            },
            {
              answer: (
                <>
                  No! Many of our students are first-time IELTS test-takers, so if you're ready to start studying now, you should go ahead and sign up! Our lessons start from the beginning and teach you everything you need to know about the IELTS, so taking the test prior is not required.
                </>
              ),
              question: 'Do I need to have taken the test before?'
            },
            {
              answer: (
                <>
                  We know that everyone has a different approach to learning, so we offer study schedules that are flexible enough to fit your own schedule. This way, you can spend more time learning what you need.
                </>
              ),
              question: 'What are your study schedules like?'
            },
            {
              answer: (
                <>
                  Extremely close. Our experienced instructors use metrics to ensure the difficulty and types of questions match those on the actual test. We rank the questions by difficulty to help you master the basics and progress towards your target score.
                </>
              ),
              question: 'How close is the material to the actual IELTS?'
            },
            {
              answer: (
                <>
                  We collect data from Magoosh students' actual scores and performance in our program. Based on these data and your performance in our program, we can predict the range of possible scores for your actual test.
                </>
              ),
              question: 'How does your score predictor work?'
            },
            {
              answer: (
                <>
                  All prices are in USD (United States Dollars).
                </>
              ),
              question: 'What currency are the prices listed in?'
            }
          ]}
        />

        <StartYourPrepSection
          examName="IELTS"
          primaryOffer={{ ...premium6MonthsOffer, name: '6 Months' }}
          secondaryOffer={{ ...premiumOffer, name: '1 Month' }}
          trialOffer={trialOffer}
        />
      </PageLayout>
    </div>
  );
};
